import { buildDrugQueryVariants, searchDrugs, DrugMapping } from '../lib/drugMappings';

export type DrugSource =
  | 'local'
  | 'extended'
  | 'extended_popular'
  | 'inn'
  | 'inn_brand'
  | 'inn_brand_contains'
  | 'inn_contains'
  | 'inn_prefix'
  | 'inn_variant'
  | 'matcher'
  | 'fda'
  | 'ema'
  | 'pmda'
  | 'external'
  | 'manual';

export interface ExtendedDrugResult {
  id: string;
  nameZh: string;
  nameEn: string;
  inn?: string;
  category?: string;
  therapeuticClass?: string;
  aliasesZh?: string[];
  aliasesEn?: string[];
  source: DrugSource;
  confidence: number;
}

function decodeLegacyEntities(value: string): string {
  return value
    .replace(/&#x27;|&#39;|&apos;/gi, "'")
    .replace(/&quot;/gi, '"')
    .replace(/&amp;/gi, '&');
}

function parseNameParts(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value.map(String).map(item => item.trim()).filter(Boolean);
  }

  const text = decodeLegacyEntities(String(value ?? '')).trim();
  if (!text.startsWith('[') || !text.endsWith(']')) {
    return [];
  }

  try {
    const parsed = JSON.parse(text.replace(/'/g, '"'));
    return Array.isArray(parsed)
      ? parsed.map(String).map(item => item.trim()).filter(Boolean)
      : [];
  } catch {
    return [];
  }
}

export function normalizeEnglishDrugField(value: unknown): string {
  const parts = parseNameParts(value);
  if (parts.length > 0) {
    return parts.map(part => part.toLowerCase().replace(/\s+/g, '_')).join('_');
  }
  return decodeLegacyEntities(String(value ?? '')).trim();
}

export function normalizeChineseDrugField(value: unknown): string {
  const parts = parseNameParts(value);
  if (parts.length > 0) {
    return parts.join('');
  }
  return decodeLegacyEntities(String(value ?? '')).trim();
}

function hasLatinText(value: unknown): boolean {
  return /[A-Za-z]/.test(String(value ?? ''));
}

function preferredEnglishDrugField(...values: unknown[]): unknown {
  return values.find(value => hasLatinText(normalizeEnglishDrugField(value))) ?? values.find(Boolean) ?? '';
}

function extractEmbeddedLatinName(value: string): string {
  const matches = value.match(/[A-Za-z][A-Za-z0-9_./+\- ]*[A-Za-z0-9]/g) || [];
  const best = matches
    .map(item => item.trim().replace(/\s+/g, ' '))
    .filter(Boolean)
    .sort((a, b) => b.length - a.length)[0];
  return best || '';
}

function extractChineseName(value: string): string {
  const latin = extractEmbeddedLatinName(value);
  const withoutLatin = latin ? value.replace(latin, '') : value;
  return withoutLatin
    .replace(/[()（）]/g, '')
    .replace(/\s+/g, '')
    .trim();
}

function preferredManualDrugName(query: string): string {
  const variants = buildDrugQueryVariants(query);
  const raw = query.trim();
  return (
    variants.find(item => item !== raw && /[\u4e00-\u9fff]/.test(item) && item.length >= 2) ||
    variants.find(item => item !== raw && item.length >= 2) ||
    variants.find(item => /[\u4e00-\u9fff]/.test(item) && item.length >= 2) ||
    variants.find(item => item.length >= 2) ||
    raw
  );
}

export function createManualDrugResult(query: string): ExtendedDrugResult | null {
  const name = preferredManualDrugName(query);
  if (name.trim().length < 2) return null;
  const embeddedEnglish = extractEmbeddedLatinName(query);
  const embeddedChinese = embeddedEnglish ? extractChineseName(query) : '';
  const nameZh = embeddedEnglish ? (embeddedChinese || name) : name;
  const nameEn = embeddedEnglish || name;

  return {
    id: `manual_${nameEn.replace(/[^\w\u4e00-\u9fff]+/g, '_')}`,
    nameZh,
    nameEn,
    inn: nameEn,
    category: '自定义输入',
    therapeuticClass: '按当前药名实时查询',
    aliasesZh: [nameZh],
    aliasesEn: embeddedEnglish ? [embeddedEnglish] : [],
    source: 'manual',
    confidence: embeddedEnglish ? 0.45 : 0.35,
  };
}

export class DrugSearchService {
  private abortController: AbortController | null = null;
  private apiBase: string;

  constructor(apiBase: string) {
    this.apiBase = apiBase;
  }

  async searchAPI(query: string): Promise<ExtendedDrugResult[]> {
    let timeoutId: number | null = null;
    try {
      if (this.abortController) {
        this.abortController.abort();
      }

      const controller = new AbortController();
      this.abortController = controller;
      timeoutId = window.setTimeout(() => controller.abort(), 2500);

      const response = await fetch(
        `${this.apiBase}/api/drugs/search?q=${encodeURIComponent(query)}&limit=15&auto_external=false`,
        {
          signal: controller.signal,
          headers: {
            'Accept': 'application/json',
          }
        }
      );

      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }

      const data = await response.json();

      return (data.results || []).map((item: any) => {
        const nameEn = normalizeEnglishDrugField(
          preferredEnglishDrugField(item.inn, item.nameEn, item.name)
        );
        const inn = normalizeEnglishDrugField(
          preferredEnglishDrugField(item.inn, nameEn, item.nameEn)
        );
        return {
          id: item.id || `api_${inn}`,
          nameZh: normalizeChineseDrugField(item.nameZh),
          nameEn,
          inn,
          category: item.category || '',
          therapeuticClass: item.therapeuticClass || '',
          aliasesZh: item.aliasesZh || [],
          aliasesEn: item.aliasesEn || [],
          source: item.source || 'inn',
          confidence: item.confidence || 0.8
        };
      });
    } catch (error) {
      if ((error as Error).name === 'AbortError') {
        return [];
      }
      console.warn('[DrugSearch] API search failed:', error);
      return [];
    } finally {
      if (timeoutId !== null) {
        window.clearTimeout(timeoutId);
      }
      if (this.abortController) {
        this.abortController = null;
      }
    }
  }

  convertToLocalFormat(drug: DrugMapping): ExtendedDrugResult {
    return {
      id: drug.id,
      nameZh: drug.nameZh,
      nameEn: drug.nameEn,
      category: drug.category,
      therapeuticClass: drug.therapeuticClass,
      aliasesZh: drug.aliasesZh,
      aliasesEn: drug.aliasesEn,
      source: 'local',
      confidence: 1.0
    };
  }

  mergeAndDeduplicateResults(
    localResults: DrugMapping[],
    apiResults: ExtendedDrugResult[]
  ): ExtendedDrugResult[] {
    const merged: ExtendedDrugResult[] = [];
    const seenIds = new Set<string>();

    localResults.forEach(drug => {
      const converted = this.convertToLocalFormat(drug);
      if (!seenIds.has(converted.nameEn.toLowerCase())) {
        seenIds.add(converted.nameEn.toLowerCase());
        merged.push(converted);
      }
    });

    apiResults.forEach(result => {
      const key = (result.inn || result.nameEn).toLowerCase();
      if (!seenIds.has(key)) {
        seenIds.add(key);
        merged.push(result);
      }
    });

    merged.sort((a, b) => {
      if (a.source === 'local' && b.source !== 'local') return -1;
      if (b.source === 'local' && a.source !== 'local') return 1;
      return b.confidence - a.confidence;
    });

    return merged;
  }

  async search(query: string): Promise<{
    results: ExtendedDrugResult[];
    sources: string[];
  }> {
    const localResults = searchDrugs(query, 8);
    const apiResults = await this.searchAPI(query);
    const merged = this.mergeAndDeduplicateResults(localResults, apiResults);
    const fallbackResult = merged.length === 0 ? createManualDrugResult(query) : null;
    const finalResults = fallbackResult ? [fallbackResult] : merged;
    const sources = [...new Set([
      ...apiResults.map(r => r.source),
      ...(fallbackResult ? [fallbackResult.source] : []),
    ])];

    return {
      results: finalResults.slice(0, 12),
      sources
    };
  }

  cancel() {
    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }
  }
}
